import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import {
  BaseAuthenticatedService,
  httpQueryParams,
  PaginatedResponse,
  RecursiveInterface,
} from './base-authenticated.service';
import { InvoiceInterface } from '@app/interface/invoice';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { environment } from '@environments/environment';
import { switchMap } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class InvoiceApi extends BaseAuthenticatedService {
  constructor(
    public override auth: AngularFireAuth,
    public override httpClient: HttpClient,
    private route: ActivatedRoute, // Inject ActivatedRoute
  ) {
    super(auth, httpClient);
  }

  public getInvoiceList<T = InvoiceInterface>(
    organization_id: number | null,
    // projectId: number,
    params: httpQueryParams = {},
  ): Observable<PaginatedResponse<T>> {
    let path = `api/${organization_id}/invoice/`;
    return this.getList<T>(path, params);
  }

  // using getRecursivePage
  public getAllInvoices<T = InvoiceInterface>(
    organization_id: number | null,
    params: httpQueryParams = {},
  ): Subject<RecursiveInterface<T>> {
    // console.log('id:', organization_id);

    const path = `api/${organization_id}/invoice/`;
    return this.getRecursivePage<T>(path, params);
  }

  public createInvoice(
    organization_id: number | null,
    invoiceData: Partial<InvoiceInterface>,
  ): Observable<InvoiceInterface> {
    // console.log('id:', organization_id);
    const path = `api/${organization_id}/invoice/`;

    // TODO: date conversion (moment.js)

    return this.post<InvoiceInterface>(path, {}, invoiceData);
  }

  public updateInvoiceDetails(
    organization_id: number | null,
    id: number,
    data: Partial<InvoiceInterface>,
  ): Observable<InvoiceInterface> {
    // console.log('data log.', data, 'id,', id);
    return this.partialUpdate<InvoiceInterface>(
      `api/${organization_id}/invoice/${id}/`,
      {}, //{ withCredentials: true },
      data,
    );
  }

  public softDeleteInvoice(
    organization_id: number | null,
    id: number,
  ): Observable<InvoiceInterface> {
    const path = `api/${organization_id}/invoice/${id}/`;
    const deleteData = {
      is_deleted: true, // Assuming your Django model has a 'deleted' boolean field
      // deleted_at: new Date().toISOString() // Optional: if you track deletion timestamp
    };

    return this.update<InvoiceInterface>(path, {}, deleteData);
  }

  public getMaxProgressiveNumber(
    organization_id: number | null,
  ): Observable<number> {
    const path = `api/${organization_id}/invoice/max_progressive/`;
    return this.get<number>(path);
  }

  // generateInvoiceXml(
  //   id: number,
  //   data: Partial<InvoiceInterface>
  // ): Observable<Blob> {
  //   const headers = new HttpHeaders({
  //     Accept: 'application/xml',
  //   });

  //   let organization_id = 1;

  //   console.log(id);

  //   return this.http.get(`${this.apiUrl}/${id}/generate-xml/`, {
  //     headers: headers,
  //     responseType: 'blob', // Important for file downloads
  //   });
  // }

  private backendDomain = environment.backendDomain;

  // The endpoints below are downloaded/previewed via plain fetch() (no Authorization
  // header, needed for direct blob/preview handling), so the backend requires a
  // short-lived signed token instead. We fetch it first (authenticated, like the rest
  // of the API) and append it as a query param on the actual download request.
  private async getInvoiceDownloadToken(invoiceId: number): Promise<string> {
    const headers = await firstValueFrom(this.getAuthHeaders());
    const headersObj: Record<string, string> = {};
    headers.keys().forEach((key) => {
      const value = headers.get(key);
      if (value !== null) {
        headersObj[key] = value;
      }
    });

    const response = await fetch(
      `${this.backendDomain}/invoice/${invoiceId}/download-token/`,
      { headers: headersObj },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.token as string;
  }

  // Method 1: Using fetch API with proper blob handling
  async downloadInvoicePdf(invoiceId: number): Promise<void> {
    try {
      const token = await this.getInvoiceDownloadToken(invoiceId);
      const response = await fetch(
        `${this.backendDomain}/invoice/${invoiceId}/pdf/?token=${encodeURIComponent(token)}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob with correct MIME type
      const blob = await response.blob();

      // Verify blob is actually a PDF
      if (blob.type !== 'application/pdf') {
        console.warn('Expected PDF but got:', blob.type);
      }

      // Extract filename from Content-Disposition header
      let filename = `Fattura ${invoiceId}.pdf`; // Fallback name

      const contentDisposition = response.headers.get('Content-Disposition');
      // console.log('contentDisposition', contentDisposition);
      if (contentDisposition) {
        const filenameMatch =
          contentDisposition.match(/filename\*=UTF-8''(.+)/i) ||
          contentDisposition.match(/filename="(.+)"/i);

        if (filenameMatch && filenameMatch[1]) {
          // Decode URI-encoded filename (especially important for UTF-8'' format)
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }

      // Create blob URL
      const blobUrl = window.URL.createObjectURL(blob);

      // Create and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      // link.download = `Fattura ${invoiceId}.pdf`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  }

  public async showPdfInDialog(invoiceId: number): Promise<string> {
    try {
      const token = await this.getInvoiceDownloadToken(invoiceId);
      // Fix the URL path - added 'api' prefix to match your other endpoints
      const response = await fetch(
        `${this.backendDomain}/invoice/${invoiceId}/pdf/?token=${encodeURIComponent(token)}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob
      const blob = await response.blob();

      // Verify it's a PDF
      if (blob.type !== 'application/pdf') {
        console.warn('Expected PDF but got:', blob.type);
      }

      // Create blob URL
      const blobUrl = window.URL.createObjectURL(blob);

      return blobUrl;
    } catch (error) {
      console.error('Error loading PDF:', error);
      throw error;
    }
  }

  async downloadInvoiceXml(invoiceId: number): Promise<void> {
    try {
      const token = await this.getInvoiceDownloadToken(invoiceId);
      const response = await fetch(
        `${this.backendDomain}/invoice/${invoiceId}/xml/?token=${encodeURIComponent(token)}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the blob with correct MIME type
      const blob = await response.blob();

      // Verify blob is actually a PDF
      if (blob.type !== 'application/xml') {
        console.warn('Expected XML but got:', blob.type);
      }

      // Extract filename from Content-Disposition header
      let filename = `Fattura_${invoiceId}.xml`; // Fallback name

      const contentDisposition = response.headers.get('Content-Disposition');
      // console.log('contentDisposition', contentDisposition);
      if (contentDisposition) {
        const filenameMatch =
          contentDisposition.match(/filename\*=UTF-8''(.+)/i) ||
          contentDisposition.match(/filename="(.+)"/i);

        if (filenameMatch && filenameMatch[1]) {
          // Decode URI-encoded filename (especially important for UTF-8'' format)
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }

      // Create blob URL
      const blobUrl = window.URL.createObjectURL(blob);

      // Create and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      // link.download = `Fattura_${invoiceId}.xml`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error downloading XML:', error);
      throw error;
    }
  }

  async getXmlContent(invoiceId: number): Promise<string> {
    // We fetch the URL. Note: Even though Django sets 'attachment',
    // fetch() will just give us the data buffer/text.
    const token = await this.getInvoiceDownloadToken(invoiceId);
    const response = await fetch(
      `${this.backendDomain}/invoice/${invoiceId}/xml/?token=${encodeURIComponent(token)}`,
    );

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`);
    }

    return await response.text();
  }

  public downloadOrganizationInvoicesZipByIds(
    organization_id: number,
    invoiceIds: number[],
  ): Observable<Blob> {
    const path = `organization/${organization_id}/zip-pdf/`;

    // Convert the array of IDs [1, 2, 3] into a string "1,2,3"
    const params = new HttpParams().set('ids', invoiceIds.join(','));

    return this.getAuthHeaders().pipe(
      switchMap((headers) => {
        return this.httpClient.get(`${this.backendDomain}/${path}`, {
          headers: headers,
          params: params,
          responseType: 'blob',
        });
      }),
    );
  }

  public downloadOrganizationInvoicesXmlZipByIds(
    organization_id: number,
    invoiceIds: number[],
  ): Observable<Blob> {
    // Assicurati che questo path corrisponda a quello definito in urls.py di Django
    const path = `organization/${organization_id}/zip-xml/`;

    // Converte l'array [1, 2, 3] in stringa "1,2,3"
    const params = new HttpParams().set('ids', invoiceIds.join(','));

    return this.getAuthHeaders().pipe(
      switchMap((headers) => {
        return this.httpClient.get(`${this.backendDomain}/${path}`, {
          headers: headers,
          params: params,
          responseType: 'blob', // Fondamentale per gestire file binari/ZIP
        });
      }),
    );
  }

  public downloadInvoicingSummaryPdf(
    organization_id: number,
    invoiceIds: number[],
    year: number,
  ): Observable<Blob> {
    // Update this path to match your actual Django URL route
    const path = `organization/${organization_id}/invoicing-summary-pdf/`;

    // Convert the array of IDs [1, 2, 3] into a string "1,2,3"
    const params = new HttpParams()
      .set('ids', invoiceIds.join(','))
      .set('year', year.toString());

    return this.getAuthHeaders().pipe(
      switchMap((headers) => {
        return this.httpClient.get(`${this.backendDomain}/${path}`, {
          headers: headers,
          params: params,
          responseType: 'blob', // Essential for PDF files
        });
      }),
    );
  }

  public uploadInvoiceXml(
    organization_id: number | null,
    xmlFile: File,
  ): Observable<any> {
    const formData = new FormData();
    formData.append('xml_file', xmlFile);

    const path = `api/${organization_id}/invoice/upload-xml/`;

    // Use getAuthHeaders with multipart/form-data - it will auto-remove Content-Type
    return this.getAuthHeaders({
      'Content-Type': 'multipart/form-data', // This will be removed by your base method
    }).pipe(
      switchMap((headers) => {
        return this.httpClient.post(
          `${environment.backendDomain}/${path}`,
          formData,
          {
            headers,
            reportProgress: true,
            observe: 'events',
          },
        );
      }),
    );
  }

  public publishToAruba(
    organization_id: number,
    invoice_id: number,
    invoiceData: InvoiceInterface,
  ): Observable<any> {
    const path = `api/${organization_id}/invoice/${invoice_id}/publish-to-aruba/`;

    // console.log(path);
    // return this.post(
    //   `${this.apiUrl}/${organizationId}/invoices/${invoiceId}/publish-to-aruba/`,
    //   {} // empty body since we're using URL parameters
    // );
    return this.post<InvoiceInterface>(path, {}, invoiceData);
  }

  async sendEmailWithPDF(
    organization_id: number,
    invoice_id: number,
    invoiceData: InvoiceInterface,
  ): Promise<void> {
    // const path = `invoice/${invoice_id}/send-email-to-company-with-pdf/`;

    // return this.post<InvoiceInterface>(path, {}, invoiceData);

    try {
      const token = await this.getInvoiceDownloadToken(invoice_id);
      const response = await fetch(
        `${this.backendDomain}/invoice/${invoice_id}/send-email-to-company-with-pdf/?token=${encodeURIComponent(token)}`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  }
}
